<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { push, replace } from 'svelte-spa-router';
	//configs
	import { devices_config } from '@devices/index';
	//helpers
	import { capFirstChar, hex_to_decimal } from 'src/helpers/utilities';
	import { isOwned } from 'src/helpers/inclusion_helpers';
	import { getLandingHref } from 'src/helpers/main_menu';
	import { getDeviceType } from 'src/helpers/add_delete_helpers';
	import { getShellyXInfoByJTI } from 'src/helpers/xt1_helpers/template_helpers';
	//services
	import {
		search_bluetooth_devices_android,
		stop_bluetooth_search_android,
	} from 'src/services/native_container_requests_android';
	import {
		search_bluetooth_devices,
		stop_bluetooth_search_ios,
	} from 'src/services/native_container_requests_ios';
	import { mobile } from 'src/services/mobile-rework';
	//stores
	import { includeDeviceModal } from 'src/stores/features/inclusion';
	import { devices } from 'src/stores/lists/devices';
import { isShowedDeviceBluetoothScan } from 'src/stores/session/alerts-onboarding';
import { app, network, os } from 'src/stores/session/native-bridge';
	//design system components
	import { Container, Alert, Button, Modal, Text, CircleAvatar } from '@Shelly/shelly-design-system';
	import { MODULES } from 'src/constants';
	import type { Device } from 'src/types/Device';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

	export let initialShow = false;
	export let priority = 20;

	const dispatch = createEventDispatcher();

	const isIOS = $app && $os === 'IOS';
	let listedDevices = [];
	let showModal = false;
	let deviceFound = false;

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('resume_app', initiateBackgroundScan);
			mobile.addEventListener('pause_app', onCloseModal);
			if (initialShow && !isActiveHomeShared()) initiateBackgroundScan();
			return () => {
				mobile.removeEventListener('resume_app', initiateBackgroundScan);
				mobile.removeEventListener('pause_app', onCloseModal);
			};
		}
	});

	const initiateBackgroundScan = () => {
		if (isActiveHomeShared()) return;
		if (!$includeDeviceModal.show && $app && $network !== 'offline') {
			scanForDevices();
			mobile.addEventListener('including_device', handleDiscoveredDevices);
		}
	};

	const scanForDevices = async () => {
		listedDevices = [];

		if (isIOS) {
			search_bluetooth_devices();
		} else {
			search_bluetooth_devices_android();
		}
	};

	const handleDiscoveredDevices = async (e: CustomEvent) => {
		let parsed_data;
		try {
			parsed_data = JSON.parse(e.detail[0]);
		} catch (error) {
			console.warn(`Invalid JSON from background bluetooth scan`, error);
			return;
		}

		if (parsed_data.status == 'deviceDiscovered' && !deviceFound) {
			const isDevice =
				typeof parsed_data?.device !== 'undefined' && typeof parsed_data?.device !== 'string';
			const device = parsed_data?.device;

			let dConfig;
			let dTypeSplit;

			if (isDevice) {
				dTypeSplit = getDeviceType(device);
			} else {
				dTypeSplit = undefined;
			}

			if (device.model || dTypeSplit) {
				//device model is for gble devices/dType is for gen 2 mostly
				Object.entries(devices_config).forEach(([i, e]) => {
					if (device?.model === i) {
						dConfig = e;
						dConfig.type = i;
					}
					if (e?.name?.toLowerCase() === dTypeSplit[0]?.toLowerCase()) {
						dConfig = e;
						dConfig.type = i;
					}
				});
			}

			if (dConfig) {
				device.jumbledID = device.id;
				// jumbledID handles jumbled Mac returned via Bluetooth scan that is returned by Gen.2 devices with firmware versions between 0.14.** to 1.0.3
				device.jumbledID = device.jumbledID
					.split(/(?=(?:..)*$)/)
					.reverse()
					.toString()
					.replace(/,/g, '');
				device.type = dConfig.type;
				device.model = device.type;

				if (device.jti) {
					const config = await getShellyXInfoByJTI(device.jti, device.model);
					device.name = config?.name || dConfig.friendly_name;
					device.service_type = config?.service_type;
					device.image = config?.image || device.image;
				} else {
					device.name = dConfig.friendly_name;
				}

				const res = await filterByOwnership(device);

				if (res && listedDevices.length < 1) {
					listedDevices.push(device);
					listedDevices = listedDevices;
					deviceFound = true;
					if (isIOS) stop_bluetooth_search_ios();
				}
			}
			console.log('%c 🦩 listedDevices', 'color: pink;', listedDevices);

			if (listedDevices.length > 0) {
				showModal = true;
			}
		}
	};

	const filterByOwnership = async (d) => {
		const deviceID =
			d.gen == 'GBLE' || String(d?.id).startsWith('XM')
				? d.id
				: d?.jti
					? 'XM' + hex_to_decimal(d.id).toString() + 'V' + d.jti.toUpperCase()
					: d.id.toLowerCase();

		const jumbledID = typeof d.jumbledID !== 'undefined' ? d.jumbledID.toLowerCase() : '';
		d.displayID = deviceID;

		if (
			typeof $devices[deviceID] === 'undefined' &&
			typeof $devices[jumbledID] === 'undefined' &&
			d.gen !== 'GBLE'
		) {
			d.displayID = deviceID;
			const res = await isOwned(deviceID);
			if (!res) {
				console.log('not mine', d.id, d.type, d.gen);
				return d;
			}
		}
	};

	const onCloseModal = () => {
		dispatch('close');
		deviceFound = false;
		isShowedDeviceBluetoothScan.set(true);
		mobile.removeEventListener('including_device', handleDiscoveredDevices);
		if (isIOS) {
			stop_bluetooth_search_ios();
		} else {
			stop_bluetooth_search_android();
		}
	};

	const handleAdd = (device) => {
		dispatch('close');
		isShowedDeviceBluetoothScan.set(true);
		includeDeviceModal.update((state) => {
			return (state = { show: true, step: 2, selected: device });
		});
	};

	const handleList = () => {
		dispatch('close');
		isShowedDeviceBluetoothScan.set(true);
		includeDeviceModal.update((state) => {
			return (state = { show: true, step: 1, selected: undefined });
		});
	};

	const closeModalOnAndroidBack = (e: CustomEvent) => {
		if (showModal) {
			e.preventDefault();
			dispatch('close');
			isShowedDeviceBluetoothScan.set(true);
		}
	};

	const getDeviceImage = (device: Device) => {
		if (/^(https?:\/\/)/.test(device.image)) {
			return device.image;
		}
		return `images/device_images/${device.service_type || device.type}.png`;
	};

	const getDeviceName = (device: Device) => {
		return `${MODULES.includes(device.type) ? '' : 'Shelly'} ${device.name}`;
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModalOnAndroidBack);
			return () => mobile.removeEventListener('back_button', closeModalOnAndroidBack);
		}
	});
</script>

<Modal
	on:close={onCloseModal}
	{showModal}
	title={$format('modal_title_new_device_suggestion', {
		default: 'New device found',
	})}
	zIndex={priority}
>
	<svelte:fragment slot="content">
		<Alert
			heading={$format('alert_info_disable_new_device_suggestion', {
				default:
					"Don't want to see such suggestions? Disable background scan from App Settings.",
			})}
			type="info"
			class="mb-m"
			cta={{
				title: capFirstChar(
					$format('alert_link_disable_new_device_suggestion', {
						default: 'Disable background scan',
					}),
				),
			}}
			on:cta_click={() => {
				onCloseModal();
				replace(getLandingHref());
				push('/settings/app?backgroundScan');
			}}
		/>

		<Container hideSlotBottom hideSlotTop padding="0">
			<!-- <Alert
			heading="Test functionality"
			isHeadingBold
			message="Currently, the functionality is only available for test users."
			type="warning"
			class="mb-m"
		/> -->
			<!-- <Text styleType="body" class="mb-m text-small text-align-center">
			{capFirstChar('Do you want to add it to your Wi-Fi network and account?')}
		</Text> -->

			{#each listedDevices as device}
				<!-- <AdvancedCard
				avatar={{ image: `images/device_images/${device.type}.png` }}
				title={`Shelly ${device.name}`}
				description={{ value: device.displayID }}
				class="width-full "
				isFocusable
			>
				<IconButton
					slot="right"
					class="align-self-center mr-s"
					icon="fas fa-plus"
					size="l"
					on:click={() => handleAdd(device)}
				/>
			</AdvancedCard> -->
				<div class="d-flex justify-content-center flex-direction-column align-items-center">
					<CircleAvatar
						class="mb-m "
						size="xxl"
						borderStatus={{ icon: 'fa-solid fa-plus', color: 'color-primary' }}
						image={getDeviceImage(device)}
						alt={getDeviceName(device)}
					/>

					<Text class="mb-xs text-align-center text-bold">
						{getDeviceName(device)}
					</Text>
					<Text class="mb-l text-align-center text-small text-color-muted">
						{`${device.displayID}`}
					</Text>
				</div>

				<Button
					variant="primary"
					class="mb-s width-full"
					size="l"
					on:click={() => handleAdd(device)}
				>
					{capFirstChar(
						$format('button_add_device', {
							default: 'add device',
						}),
					)}
				</Button>
			{/each}

			<Button variant="secondary" class="mb-m width-full" size="l" on:click={handleList}>
				{capFirstChar(
					$format('button_more_discovered', {
						default: 'see more discovered devices',
					}),
				)}
			</Button>

			<Text
				element="div"
				class="d-flex link-styles justify-content-center text-small"
				on:click={() => {
					onCloseModal();
				}}
			>
				{capFirstChar(
					$format('button_maybe_later', {
						default: 'maybe later',
					}),
				)}
			</Text>
		</Container>
	</svelte:fragment>
</Modal>
